import { useState, useEffect } from 'react';
import { useT } from '../../context/TenantContext';
import { api } from '../../services/api';
import { Button } from '../../components/ui';

const ACCENT = '#0085CA';
const NAVY   = '#003D6B';
const LIGHT  = '#EEF6FC';

function MIcon({ name, size = 18, style = {} }) {
  return <span className="material-symbols-rounded" style={{ fontSize: size, lineHeight: 1, verticalAlign: 'middle', ...style }}>{name}</span>;
}

const inputSt  = { padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 7, fontSize: 13, outline: 'none', background: '#fff', boxSizing: 'border-box' };

function Label({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 5 }}>{children}</div>;
}

function Select({ value, onChange, children, style = {} }) {
  return (
    <div style={{ position: 'relative', display: 'inline-block', ...style }}>
      <select value={value} onChange={onChange} style={{ ...inputSt, width: '100%', appearance: 'none', WebkitAppearance: 'none', paddingRight: 28, cursor: 'pointer' }}>{children}</select>
      <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9CA3AF', fontSize: 10 }}>▾</span>
    </div>
  );
}

function StatCard({ icon, label, value, color = ACCENT }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', flex: 1, minWidth: 130, borderTop: `3px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <MIcon name={icon} size={14} style={{ color }} />
        <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5 }}>{label}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: NAVY }}>{value ?? '—'}</div>
    </div>
  );
}

export default function ReportsTab({ rooms: roomsProp, categories: categoriesProp }) {
  const { t } = useT();
  const [rooms,      setRooms]      = useState(roomsProp || []);
  const [categories, setCategories] = useState(categoriesProp || []);
  const [tariffs,    setTariffs]    = useState([]);
  const [saved,      setSaved]      = useState([]);
  const [report,     setReport]     = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [subtab,     setSubtab]     = useState('report');
  const [saveModal,  setSaveModal]  = useState(false);
  const [saveName,   setSaveName]   = useState('');

  const [filters, setFilters] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0,10),
    date_to:   new Date().toISOString().slice(0,10),
    category_ids: [], room_ids: [], status: 'confirmed',
    requester_email: '', calc_mode: 'hours', include_pricing: false, group_by: 'category',
  });
  const ff = v => setFilters(p => ({ ...p, ...v }));
  const toggleArr = (key, val) => ff({ [key]: filters[key].includes(val) ? filters[key].filter(x=>x!==val) : [...filters[key], val] });

  function setQuickPeriod(type) {
    const d = new Date();
    if (type==='month') ff({ date_from: new Date(d.getFullYear(),d.getMonth(),1).toISOString().slice(0,10), date_to: d.toISOString().slice(0,10) });
    if (type==='prev')  { const f=new Date(d.getFullYear(),d.getMonth()-1,1); const t=new Date(d.getFullYear(),d.getMonth(),0); ff({ date_from:f.toISOString().slice(0,10), date_to:t.toISOString().slice(0,10) }); }
    if (type==='year')  ff({ date_from:`${d.getFullYear()}-01-01`, date_to:d.toISOString().slice(0,10) });
    if (type==='week')  { const f=new Date(d); f.setDate(d.getDate()-7); ff({ date_from:f.toISOString().slice(0,10), date_to:d.toISOString().slice(0,10) }); }
  }

  useEffect(() => {
    const tok = { headers: { Authorization: `Bearer ${localStorage.getItem('hd_token')}` } };
    if (!roomsProp?.length)      api.getRooms().then(setRooms);
    if (!categoriesProp?.length) api.getRoomCategories().then(setCategories);
    fetch('/api/reports/tariffs', tok).then(r=>r.json()).then(t => setTariffs(Array.isArray(t)?t:[]));
    fetch('/api/reports/saved',   tok).then(r=>r.json()).then(s => setSaved(Array.isArray(s)?s:[]));
  }, []);

  async function runReport() {
    setLoading(true);
    try {
      const res = await fetch('/api/reports/generate', { method:'POST', headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('hd_token')}`}, body:JSON.stringify(filters) });
      setReport(await res.json());
    } finally { setLoading(false); }
  }

  async function exportCSV() {
    const res = await fetch('/api/reports/export/csv', { method:'POST', headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('hd_token')}`}, body:JSON.stringify(filters) });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    Object.assign(document.createElement('a'), { href:url, download:`informe-${filters.date_from}-${filters.date_to}.csv` }).click();
    URL.revokeObjectURL(url);
  }

  async function doSave() {
    if (!saveName.trim()) return;
    const res = await fetch('/api/reports/saved', { method:'POST', headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('hd_token')}`}, body:JSON.stringify({name:saveName,filters}) });
    const saved2 = await res.json();
    setSaved(p => [saved2, ...p]);
    setSaveModal(false); setSaveName('');
  }

  const filteredRooms = filters.category_ids.length ? rooms.filter(r=>filters.category_ids.includes(r.category_id)) : rooms;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 6 }}>
        {[['report','bar_chart','Informe'],['tariffs','payments','Tarifas'],['saved','bookmark',`Guardados (${saved.length})`]].map(([t,icon,label]) => (
          <button key={t} onClick={()=>setSubtab(t)} style={{
            padding:'6px 14px', borderRadius:7, border:'1px solid '+(subtab===t?ACCENT:'#E5E7EB'),
            background: subtab===t?LIGHT:'#fff', color:subtab===t?ACCENT:'#6B7280',
            fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:4,
          }}>
            <MIcon name={icon} size={13}/>{label}
          </button>
        ))}
      </div>

      {/* ── Informe ── */}
      {subtab === 'report' && (
        <div style={{ display:'flex', gap:16, alignItems:'stretch', width:'100%', minHeight:600 }}>
          {/* Filtros */}
          <div style={{ width:300, flexShrink:0, background:'#fff', borderRadius:10, border:'1px solid #F3F4F6', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', gap:8 }}>
              <MIcon name="filter_list" size={14} style={{color:ACCENT}}/>
              <span style={{fontSize:13,fontWeight:700,color:NAVY}}>Filtros</span>
            </div>
            <div style={{padding:16,display:'flex',flexDirection:'column',gap:14,overflowY:'auto',flex:1}}>
              {/* Período */}
              <div>
                <Label>Período</Label>
                <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:6}}>
                  <input type="date" value={filters.date_from} onChange={e=>ff({date_from:e.target.value})} style={{...inputSt,flex:1,fontSize:12}}/>
                  <span style={{color:'#D1D5DB'}}>—</span>
                  <input type="date" value={filters.date_to}   onChange={e=>ff({date_to:e.target.value})}   style={{...inputSt,flex:1,fontSize:12}}/>
                </div>
                <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                  {[['week','Semana'],['month','Mes'],['prev','Mes ant.'],['year','Año']].map(([k,l])=>(
                    <button key={k} onClick={()=>setQuickPeriod(k)} style={{padding:'2px 7px',borderRadius:10,border:'1px solid #E5E7EB',background:'#F9FAFB',fontSize:11,cursor:'pointer',color:'#6B7280'}}>{l}</button>
                  ))}
                </div>
              </div>
              {/* Categorías */}
              <div>
                <Label>Categorías</Label>
                <div style={{display:'flex',flexDirection:'column',gap:4}}>
                  {categories.map(cat=>(
                    <label key={cat.id} style={{display:'flex',alignItems:'center',gap:7,cursor:'pointer',padding:'4px 7px',borderRadius:6,background:filters.category_ids.includes(cat.id)?LIGHT:'transparent',border:'1px solid '+(filters.category_ids.includes(cat.id)?ACCENT+'33':'transparent')}}>
                      <input type="checkbox" checked={filters.category_ids.includes(cat.id)} onChange={()=>toggleArr('category_ids',cat.id)} style={{accentColor:ACCENT}}/>
                      <span style={{width:8,height:8,borderRadius:'50%',background:cat.color,flexShrink:0}}/>
                      <span style={{fontSize:12,color:'#374151'}}>{cat.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              {/* Salas */}
              <div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
                  <Label>Salas</Label>
                  {filters.room_ids.length>0 && <button onClick={()=>ff({room_ids:[]})} style={{fontSize:10,color:'#B91C1C',background:'none',border:'none',cursor:'pointer',padding:0}}>Limpiar</button>}
                </div>
                <div style={{maxHeight:120,overflowY:'auto',display:'flex',flexDirection:'column',gap:2}}>
                  {filteredRooms.map(r=>(
                    <label key={r.id} style={{display:'flex',alignItems:'center',gap:7,cursor:'pointer',fontSize:12,color:'#374151',padding:'2px 4px',borderRadius:4,background:filters.room_ids.includes(r.id)?LIGHT:'transparent'}}>
                      <input type="checkbox" checked={filters.room_ids.includes(r.id)} onChange={()=>toggleArr('room_ids',r.id)} style={{accentColor:ACCENT}}/>
                      {r.name}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{borderTop:'1px solid #F3F4F6',paddingTop:12,display:'flex',flexDirection:'column',gap:10}}>
                <div>
                  <Label>Estado</Label>
                  <Select value={filters.status} onChange={e=>ff({status:e.target.value})} style={{width:'100%'}}>
                    <option value="all">Todas</option>
                    <option value="confirmed">Confirmadas</option>
                    <option value="pending">Pendientes</option>
                  </Select>
                </div>
                <div>
                  <Label>Agrupar por</Label>
                  <Select value={filters.group_by} onChange={e=>ff({group_by:e.target.value})} style={{width:'100%'}}>
                    <option value="category">Categoría</option>
                    <option value="room">Sala</option>
                    <option value="requester">Solicitante</option>
                    <option value="none">Sin agrupar</option>
                  </Select>
                </div>
                <div>
                  <Label>Duración</Label>
                  <div style={{display:'flex',borderRadius:7,border:'1px solid #E5E7EB',overflow:'hidden'}}>
                    {[['hours',t('reports.hours')],['days',t('reports.days')]].map(([v,l])=>(
                      <button key={v} onClick={()=>ff({calc_mode:v})} style={{flex:1,padding:'7px',border:'none',background:filters.calc_mode===v?ACCENT:'#fff',color:filters.calc_mode===v?'#fff':'#6B7280',fontSize:12,fontWeight:600,cursor:'pointer'}}>{l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Solicitante (email)</Label>
                  <input value={filters.requester_email} onChange={e=>ff({requester_email:e.target.value})} placeholder="Filtrar por email…" style={{...inputSt,width:'100%',fontSize:12}}/>
                </div>
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'8px',borderRadius:7,border:'1px solid '+(filters.include_pricing?ACCENT+'44':'#E5E7EB'),background:filters.include_pricing?LIGHT:'#F9FAFB'}}>
                  <input type="checkbox" checked={filters.include_pricing} onChange={e=>ff({include_pricing:e.target.checked})} style={{accentColor:ACCENT,width:14,height:14}}/>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:'#374151'}}>Incluir importes</div>
                    <div style={{fontSize:11,color:'#9CA3AF'}}>Requiere tarifas</div>
                  </div>
                </label>
              </div>
              <Button variant="primary" onClick={runReport} disabled={loading} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                <MIcon name={loading?'hourglass_empty':'play_arrow'} size={16}/>
                {loading?t('general.loading'):t('reports.generate')}
              </Button>
            </div>
          </div>

          {/* Resultados */}
          <div style={{flex:1,minWidth:0,background:'#fff',borderRadius:10,border:'1px solid #F3F4F6',padding:20,display:'flex',flexDirection:'column',gap:14,overflow:'hidden'}}>
            {!report && !loading && (
              <div style={{padding:'60px 0',textAlign:'center'}}>
                <MIcon name="bar_chart" size={48} style={{display:'block',margin:'0 auto 10px',color:'#E5E7EB'}}/>
                <div style={{fontSize:14,fontWeight:600,color:'#9CA3AF'}}>Configura los filtros y genera el informe</div>
                <div style={{fontSize:12,color:'#D1D5DB',marginTop:4}}>Los resultados aparecerán aquí</div>
              </div>
            )}
            {report && (
              <>
                <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                  <Button variant="secondary" onClick={()=>setSaveModal(true)} style={{display:'flex',alignItems:'center',gap:4}}>
                    <MIcon name="bookmark_add" size={13}/>Guardar filtros
                  </Button>
                  <button onClick={exportCSV} style={{padding:'6px 14px',borderRadius:7,border:'none',background:'#059669',color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                    <MIcon name="download" size={13}/>Exportar CSV
                  </button>
                </div>
                <div style={{display:'flex',gap:12}}>
                  <StatCard icon="event_available" label="Reservas"      value={report.summary.total_bookings}/>
                  <StatCard icon="schedule"         label="Horas totales" value={`${report.summary.total_hours}h`} color="#7C3AED"/>
                  <StatCard icon="calendar_today"   label="Días totales"  value={`${report.summary.total_days}d`}  color="#D97706"/>
                  {report.summary.total_amount!=null && <StatCard icon="payments" label="Importe" value={`${report.summary.total_amount.toFixed(2)} €`} color="#059669"/>}
                </div>
                {filters.group_by!=='none' && <GroupTable filters={filters} report={report} t={t}/>}
                <DetailTable rows={report.rows} calcMode={filters.calc_mode} includePricing={filters.include_pricing} t={t}/>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Tarifas ── */}
      {subtab === 'tariffs' && <TariffsTab tariffs={tariffs} setTariffs={setTariffs} rooms={rooms} categories={categories} t={t}/>}

      {/* ── Guardados ── */}
      {subtab === 'saved' && (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {saved.length===0 && <div style={{background:'#fff',borderRadius:10,padding:'48px 0',textAlign:'center',color:'#9CA3AF',fontSize:13}}>No hay informes guardados</div>}
          {saved.map(s=>(
            <div key={s.id} style={{background:'#fff',borderRadius:10,padding:'14px 20px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:NAVY}}>{s.name}</div>
                <div style={{fontSize:12,color:'#9CA3AF',marginTop:2}}>{s.created_at?.slice(0,10)}</div>
              </div>
              <div style={{display:'flex',gap:8}}>
                <Button variant="ghost" onClick={()=>{setFilters(JSON.parse(s.filters));setSubtab('report');}}>Cargar</Button>
                <Button variant="danger" onClick={async()=>{await fetch(`/api/reports/saved/${s.id}`,{method:'DELETE',headers:{Authorization:`Bearer ${localStorage.getItem('hd_token')}`}});setSaved(p=>p.filter(x=>x.id!==s.id));}}>
                  <MIcon name="delete" size={14}/>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal guardar */}
      {saveModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:360,boxShadow:'0 8px 40px rgba(0,0,0,.2)'}}>
            <div style={{fontSize:15,fontWeight:700,color:NAVY,marginBottom:16}}>Guardar configuración de informe</div>
            <input value={saveName} onChange={e=>setSaveName(e.target.value)} placeholder="Nombre del informe…" autoFocus onKeyDown={e=>e.key==='Enter'&&doSave()}
              style={{...inputSt,width:'100%',marginBottom:16,boxSizing:'border-box'}}/>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <Button variant="secondary" onClick={()=>setSaveModal(false)}>Cancelar</Button>
              <Button variant="primary" onClick={doSave}>Guardar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tabla agrupada ──────────────────────────────────────
function GroupTable({ filters, report, t }) {
  const { group_by, calc_mode, include_pricing } = filters;
  const th = { padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:.4, borderBottom:'1px solid #F3F4F6', whiteSpace:'nowrap' };
  const td = { padding:'10px 14px', fontSize:13, color:'#374151', borderBottom:'1px solid #F9FAFB' };
  const fmt = (row, isAmt) => isAmt ? (row.amount>0?`${Math.round(row.amount*100)/100} €`:'—') : (calc_mode==='hours'?`${Math.round(row.hours*100)/100}h`:`${Math.round(row.days*100)/100}d`);

  let rows = [];
  if (group_by==='category') rows = report.by_category;
  else if (group_by==='room') rows = report.by_room;
  else if (group_by==='requester') {
    const m = {};
    report.rows.forEach(r=>{ const k=r.requester_email||r.requester_name; if(!m[k]) m[k]={name:r.requester_name,email:r.requester_email,count:0,hours:0,days:0,amount:0}; m[k].count++;m[k].hours+=r.duration_hours;m[k].days+=r.duration_days;if(r.amount)m[k].amount+=r.amount; });
    rows = Object.values(m).sort((a,b)=>b.count-a.count);
  }

  const label = group_by==='category'?'categoría':group_by==='room'?'sala':'solicitante';
  return (
    <div style={{background:'#fff',borderRadius:10,overflow:'hidden'}}>
      <div style={{padding:'12px 16px',borderBottom:'1px solid #F3F4F6',fontSize:13,fontWeight:700,color:NAVY}}>
        <MIcon name="table_chart" size={14} style={{marginRight:6,color:'#9CA3AF'}}/>Resumen por {label}
      </div>
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr>
            {group_by==='category'&&<><th style={th}>Categoría</th><th style={th}>Reservas</th><th style={th}>{calc_mode==='hours'?t('reports.hours'):t('reports.days')}</th>{include_pricing&&<th style={th}>Importe</th>}</>}
            {group_by==='room'&&<><th style={th}>Sala</th><th style={th}>Categoría</th><th style={th}>Ubicación</th><th style={th}>Reservas</th><th style={th}>{calc_mode==='hours'?t('reports.hours'):t('reports.days')}</th>{include_pricing&&<th style={th}>Importe</th>}</>}
            {group_by==='requester'&&<><th style={th}>Solicitante</th><th style={th}>Email</th><th style={th}>Reservas</th><th style={th}>{calc_mode==='hours'?t('reports.hours'):t('reports.days')}</th>{include_pricing&&<th style={th}>Importe</th>}</>}
          </tr></thead>
          <tbody>
            {rows.map((row,i)=>(
              <tr key={i} style={{background:i%2===0?'#fff':'#F9FAFB'}}>
                {group_by==='category'&&<><td style={td}><span style={{width:8,height:8,borderRadius:'50%',background:row.color,display:'inline-block',marginRight:8}}/><b>{row.name}</b></td><td style={{...td,fontWeight:700,color:'#0085CA'}}>{row.count}</td><td style={td}>{fmt(row)}</td>{include_pricing&&<td style={{...td,color:'#059669',fontWeight:600}}>{fmt(row,true)}</td>}</>}
                {group_by==='room'&&<><td style={{...td,fontWeight:600}}>{row.name}</td><td style={td}><span style={{fontSize:11,background:'#F3F4F6',padding:'2px 7px',borderRadius:8}}>{row.category}</span></td><td style={{...td,color:'#9CA3AF'}}>{row.location||'—'}</td><td style={{...td,fontWeight:700,color:'#0085CA'}}>{row.count}</td><td style={td}>{fmt(row)}</td>{include_pricing&&<td style={{...td,color:'#059669',fontWeight:600}}>{fmt(row,true)}</td>}</>}
                {group_by==='requester'&&<><td style={{...td,fontWeight:600}}>{row.name}</td><td style={{...td,color:'#9CA3AF'}}>{row.email}</td><td style={{...td,fontWeight:700,color:'#0085CA'}}>{row.count}</td><td style={td}>{fmt(row)}</td>{include_pricing&&<td style={{...td,color:'#059669',fontWeight:600}}>{fmt(row,true)}</td>}</>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tabla detallada ─────────────────────────────────────
function DetailTable({ rows, calcMode, includePricing, t }) {
  const visible = rows;
  const th = { padding:'9px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:.4, borderBottom:'1px solid #F3F4F6', whiteSpace:'nowrap' };
  const td = { padding:'8px 14px', fontSize:12, color:'#374151', borderBottom:'1px solid #F9FAFB' };
  return (
    <div style={{borderRadius:10,overflow:'hidden',border:'1px solid #F3F4F6'}}>
      <div style={{padding:'12px 16px',borderBottom:'1px solid #F3F4F6',display:'flex',justifyContent:'space-between',alignItems:'center',background:'#fff'}}>
        <div style={{fontSize:13,fontWeight:700,color:NAVY}}><MIcon name="list_alt" size={14} style={{marginRight:6,color:'#9CA3AF'}}/>Detalle <span style={{color:'#9CA3AF',fontWeight:400}}>({rows.length} reservas)</span></div>
      </div>
      <div style={{overflowX:'auto',overflowY:'auto',maxHeight:420,background:'#fff'}}>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead><tr>
            {['Título','Solicitante',t('reports.rooms'),t('reports.categories'),'Inicio','Fin',calcMode==='hours'?t('reports.hours'):t('reports.days'),'Estado',...(includePricing?['Importe']:[])].map(h=><th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {visible.map((r,i)=>(
              <tr key={r.id} style={{background:i%2===0?'#fff':'#F9FAFB'}}>
                <td style={{...td,fontWeight:600,maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.title}</td>
                <td style={td}><div style={{fontWeight:500}}>{r.requester_name}</div><div style={{fontSize:11,color:'#9CA3AF'}}>{r.requester_email}</div></td>
                <td style={{...td,fontWeight:500}}>{r.room_name}</td>
                <td style={td}><span style={{background:(r.category_color||'#ccc')+'22',color:r.category_color||'#666',padding:'2px 7px',borderRadius:10,fontSize:11,fontWeight:700}}>{r.category_name}</span></td>
                <td style={{...td,color:'#6B7280',fontSize:11}}>{r.start_time?.slice(0,16).replace('T',' ')}</td>
                <td style={{...td,color:'#6B7280',fontSize:11}}>{r.end_time?.slice(0,16).replace('T',' ')}</td>
                <td style={{...td,fontWeight:700,color:NAVY}}>{calcMode==='hours'?`${r.duration_hours}h`:`${r.duration_days}d`}</td>
                <td style={td}><span style={{padding:'2px 7px',borderRadius:10,fontSize:11,fontWeight:700,background:r.status==='confirmed'?'#D1FAE5':'#FEF3C7',color:r.status==='confirmed'?'#065F46':'#92400E'}}>{r.status==='confirmed'?'Confirmada':'Pendiente'}</span></td>
                {includePricing&&<td style={{...td,color:'#059669',fontWeight:600}}>{r.amount!=null?`${r.amount} ${r.currency}`:'—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

// ── Tarifas ─────────────────────────────────────────────
function TariffsTab({ tariffs, setTariffs, rooms, categories, t }) {
  const [form,setForm]=useState({name:'',room_id:'',category_id:'',price_hour:'',price_day:'',currency:'EUR',notes:''});
  const [editing,setEditing]=useState(null);
  const ff=v=>setForm(p=>({...p,...v}));
  const st={padding:'7px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,outline:'none',width:'100%',boxSizing:'border-box'};
  const th={padding:'10px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:.4,borderBottom:'1px solid #F3F4F6'};
  const td={padding:'10px 14px',fontSize:13,color:'#374151',borderBottom:'1px solid #F9FAFB'};

  async function save() {
    const method=editing?'PUT':'POST'; const url=editing?`/api/reports/tariffs/${editing}`:'/api/reports/tariffs';
    const res=await fetch(url,{method,headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('hd_token')}`},body:JSON.stringify({...form,room_id:form.room_id||null,category_id:form.category_id||null,price_hour:form.price_hour?Number(form.price_hour):null,price_day:form.price_day?Number(form.price_day):null})});
    const t=await res.json();
    setTariffs(p=>editing?p.map(x=>x.id===editing?t:x):[t,...p]);
    setForm({name:'',room_id:'',category_id:'',price_hour:'',price_day:'',currency:'EUR',notes:''});setEditing(null);
  }
  async function del(id) { await fetch(`/api/reports/tariffs/${id}`,{method:'DELETE',headers:{Authorization:`Bearer ${localStorage.getItem('hd_token')}`}}); setTariffs(p=>p.filter(t=>t.id!==id)); }

  return (
    <div style={{display:'grid',gridTemplateColumns:'300px 1fr',gap:16,alignItems:'start'}}>
      <div style={{background:'#fff',borderRadius:10,padding:20,display:'flex',flexDirection:'column',gap:12}}>
        <div style={{fontSize:13,fontWeight:700,color:NAVY}}>{editing?'Editar tarifa':'Nueva tarifa'}</div>
        <div style={{fontSize:12,color:'#9CA3AF'}}>Las tarifas son opcionales. Sin tarifas, los informes solo muestran datos de uso.</div>
        {[['Nombre','name','text','Ej. Pistas deportivas'],['€/hora','price_hour','number','0.00'],['€/día','price_day','number','0.00']].map(([label,key,type,ph])=>(
          <div key={key}><div style={{fontSize:11,fontWeight:600,color:'#6B7280',textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>{label}</div><input type={type} value={form[key]} onChange={e=>ff({[key]:e.target.value})} placeholder={ph} style={st}/></div>
        ))}
        <div><div style={{fontSize:11,fontWeight:600,color:'#6B7280',textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>Sala específica</div><select value={form.room_id} onChange={e=>ff({room_id:e.target.value,category_id:''})} style={{...st,cursor:'pointer'}}><option value="">— Por categoría —</option>{rooms.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}</select></div>
        <div><div style={{fontSize:11,fontWeight:600,color:'#6B7280',textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>Categoría</div><select value={form.category_id} onChange={e=>ff({category_id:e.target.value,room_id:''})} style={{...st,cursor:'pointer'}} disabled={!!form.room_id}><option value="">— Seleccionar —</option>{categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        <div><div style={{fontSize:11,fontWeight:600,color:'#6B7280',textTransform:'uppercase',letterSpacing:.4,marginBottom:4}}>Moneda</div><select value={form.currency} onChange={e=>ff({currency:e.target.value})} style={{...st,cursor:'pointer'}}>{['EUR','USD','GBP','COP','DOP','AOA'].map(c=><option key={c}>{c}</option>)}</select></div>
        <div style={{display:'flex',gap:8,paddingTop:4}}>
          {editing&&<Button variant="secondary" onClick={()=>{setEditing(null);setForm({name:'',room_id:'',category_id:'',price_hour:'',price_day:'',currency:'EUR',notes:''});}} style={{flex:1}}>Cancelar</Button>}
          <Button variant="primary" onClick={save} style={{flex:1}}>{editing?'Guardar cambios':'Añadir tarifa'}</Button>
        </div>
      </div>
      <div style={{background:'#fff',borderRadius:10,overflow:'hidden'}}>
        <div style={{padding:'12px 16px',borderBottom:'1px solid #F3F4F6',fontSize:13,fontWeight:700,color:NAVY}}>Tarifas <span style={{color:'#9CA3AF',fontWeight:400}}>({tariffs.length})</span></div>
        {tariffs.length===0?<div style={{padding:'48px 0',textAlign:'center',color:'#9CA3AF',fontSize:13}}><MIcon name="payments" size={36} style={{display:'block',margin:'0 auto 8px',color:'#E5E7EB'}}/>Sin tarifas configuradas</div>:(
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Nombre','Aplica a','€/hora','€/día','Moneda',''].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{tariffs.map((t,i)=>(
              <tr key={t.id} style={{background:i%2===0?'#fff':'#F9FAFB'}}>
                <td style={{...td,fontWeight:600}}>{t.name}</td>
                <td style={td}>{t.room_name?`Sala: ${t.room_name}`:t.category_name?`Cat: ${t.category_name}`:'Global'}</td>
                <td style={td}>{t.price_hour!=null?`${t.price_hour} ${t.currency}`:'—'}</td>
                <td style={td}>{t.price_day!=null?`${t.price_day} ${t.currency}`:'—'}</td>
                <td style={td}>{t.currency}</td>
                <td style={td}><div style={{display:'flex',gap:6}}>
                  <Button variant="secondary" size="sm" onClick={()=>{setEditing(t.id);setForm({name:t.name,room_id:t.room_id||'',category_id:t.category_id||'',price_hour:t.price_hour??'',price_day:t.price_day??'',currency:t.currency,notes:t.notes||''});}}>Editar</Button>
                  <Button variant="danger" size="sm" onClick={()=>del(t.id)}><MIcon name="delete" size={13}/></Button>
                </div></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
